import React, { useEffect, useState, useRef, Fragment } from 'react';
import { trackFbEvent } from '../utils/fbPixel';
const withoutPoints = ['Estudiás de cero cada parcial', 'Finales acumulados sin saber por dónde empezar', 'Fórmulas y cálculos en 5 libros distintos', 'Materias recursadas por falta de estructura', 'Sin saber qué te van a tomar en el final'];
const withPoints = ['Resúmenes y criterios listos para repasar', 'Guía de estudio + banco de preguntas por materia', 'Vademécum de fórmulas en una sola hoja', 'Sistema de estudio inverso → aprobás de primera', 'Banco de preguntas armado con lo que más toman'];
const TIMER_KEY = 'megapack_nutricion_countdown_end';
const DURATION_MS = 24 * 60 * 60 * 1000;
function getEndTime(): number {
  if (typeof window === 'undefined') return Date.now() + DURATION_MS;
  const stored = localStorage.getItem(TIMER_KEY);
  if (stored) {
    const end = parseInt(stored, 10);
    if (end > Date.now()) return end;
  }
  const end = Date.now() + DURATION_MS;
  localStorage.setItem(TIMER_KEY, String(end));
  return end;
}
function getTimeLeft(endTime: number) {
  const diff = Math.max(0, endTime - Date.now());
  return {
    hours: Math.floor(diff / (1000 * 60 * 60)),
    minutes: Math.floor(diff % (1000 * 60 * 60) / (1000 * 60)),
    seconds: Math.floor(diff % (1000 * 60) / 1000)
  };
}
export function DarkCTA() {
  const [endTime] = useState<number>(() => getEndTime());
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(endTime));
  const sectionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const node = sectionRef.current;
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer === null && document.visibilityState === 'visible') {
        setTimeLeft(getTimeLeft(endTime));
        timer = setInterval(() => setTimeLeft(getTimeLeft(endTime)), 1000);
      }
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    let visible = true;
    let observer: IntersectionObserver | null = null;
    if (node && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        if (visible) start();else stop();
      }, {
        threshold: 0
      });
      observer.observe(node);
    } else {
      start();
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && visible) start();else stop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [endTime]);
  const pad = (n: number) => String(n).padStart(2, '0');
  return <section ref={sectionRef} className="w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-gray-900 text-base leading-6" style={{
    background: 'linear-gradient(180deg, rgb(240,253,244) 0%, rgb(255,255,255) 30%, rgb(255,255,255) 70%, rgb(240,253,244) 100%)'
  }} data-id="element-80">
      <div className="mx-auto max-w-4xl text-center" data-id="element-81">
        {/* Brand */}
        <div className="mb-8 sm:text-3xl font-black tracking-[3px] text-green-700 text-[18px]" data-id="element-82">
          🥗 MEGAPACK NUTRICIÓN CARRERA RESUELTA™
        </div>

        {/* Headlines */}
        <h2 className="text-xl sm:text-3xl font-bold text-gray-900 m-0 mb-3" data-id="element-83">
          El próximo parcial podés volver a improvisar.
        </h2>
        <p className="text-xl sm:text-3xl font-black text-green-700 m-0 mb-10" data-id="element-84">
          O podés entrar con todo resuelto desde hoy.
        </p>

        {/* Comparison Grid */}
        <div className="mx-auto max-w-2xl mb-12" data-id="element-85">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400 m-0 mb-4" data-id="element-86">
            ¿Cuánto cuesta NO tenerlo?
          </p>
          <div className="grid grid-cols-2 gap-3" data-id="element-87">
            {/* Sin Megapack */}
            <div className="rounded-xl overflow-hidden" style={{
            border: '1px solid rgb(229,231,235)'
          }} data-id="element-88">
              <div className="px-4 py-2.5 border-b border-gray-100" style={{
              backgroundColor: 'rgb(249,250,251)'
            }} data-id="element-89">
                <span className="text-xs font-black text-gray-400 uppercase tracking-wide" data-id="element-90">
                  ❌ Sin Megapack
                </span>
              </div>
              <div className="px-4 py-3 flex flex-col gap-2.5" style={{
              backgroundColor: 'rgb(255,255,255)'
            }} data-id="element-91">
                {withoutPoints.map((point, i) => <div key={i} className="flex items-start gap-2 text-left" data-id="element-92">
                    <span className="shrink-0 text-red-400 text-xs mt-0.5" data-id="element-93">
                      ✗
                    </span>
                    <span className="text-xs text-gray-500 leading-snug" data-id="element-94">
                      {point}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Con Megapack */}
            <div className="rounded-xl overflow-hidden" style={{
            border: '1px solid rgba(34,197,94,0.3)'
          }} data-id="element-95">
              <div className="px-4 py-2.5 border-b border-green-700/10" style={{
              backgroundColor: 'rgb(220,252,231)'
            }} data-id="element-96">
                <span className="text-xs font-black text-green-700 uppercase tracking-wide" data-id="element-97">
                  ✅ Con Megapack
                </span>
              </div>
              <div className="px-4 py-3 flex flex-col gap-2.5" style={{
              backgroundColor: 'rgb(240,253,244)'
            }} data-id="element-98">
                {withPoints.map((point, i) => <div key={i} className="flex items-start gap-2 text-left" data-id="element-99">
                    <span className="shrink-0 text-green-600 text-xs mt-0.5" data-id="element-100">
                      ✓
                    </span>
                    <span className="text-xs font-bold text-gray-800 leading-snug" data-id="element-101">
                      {point}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Countdown */}
        <div className="mb-4" data-id="element-102">
          <p className="font-bold text-gray-900 m-0 mb-4" data-id="element-103">
            ⏰ El precio de lanzamiento cierra en:
          </p>
          <div className="flex items-center justify-center gap-0" data-id="element-104">
            {[{
            value: timeLeft.hours,
            label: 'hs'
          }, {
            value: timeLeft.minutes,
            label: 'min'
          }, {
            value: timeLeft.seconds,
            label: 'seg'
          }].map((item, i) => <Fragment key={item.label} data-id="element-105">
                <div className="flex flex-col items-center" data-id="element-106">
                  <span className="text-5xl sm:text-7xl font-black tabular-nums" style={{
                color: 'rgb(255,68,68)'
              }} data-id="element-107">
                    {pad(item.value)}
                  </span>
                  <span className="text-xs sm:text-sm uppercase tracking-wide text-gray-500" data-id="element-108">
                    {item.label}
                  </span>
                </div>
                {i < 2 && <span className="mb-4 text-5xl sm:text-7xl font-black" style={{
              color: 'rgb(255,68,68)'
            }} data-id="element-109">
                    :
                  </span>}
              </Fragment>)}
          </div>
        </div>

        <p className="text-sm text-gray-600 m-0 mb-12" data-id="element-110">
          Después sube{' '}
          <span className="font-bold text-red-500" data-id="element-111">$15.000 ARS</span> y no
          vuelve.
        </p>

        {/* Two Pricing Cards */}
        <div className="mx-auto max-w-2xl mb-10 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-112">
          {/* Base */}
          <div className="flex flex-col rounded-xl p-6 border border-gray-200 shadow-sm" style={{
          backgroundColor: 'rgb(255,255,255)'
        }} data-id="element-113">
            <div className="mb-2 text-2xl" data-id="element-114">✅</div>
            <h3 className="text-base font-black text-gray-900 m-0 mb-1" data-id="element-115">
              MEGAPACK NUTRICIÓN CARRERA RESUELTA™
            </h3>
            <p className="text-xs text-gray-500 m-0 mb-4" data-id="element-116">
              6 Tomos + 6 Bonos · Toda la carrera organizada
            </p>
            <div className="mt-auto" data-id="element-117">
              <p className="text-sm text-gray-500 line-through m-0 mb-1" data-id="element-118">
                Antes: $58.990 ARS
              </p>
              <p className="text-2xl font-black text-gray-900 m-0 mb-4" data-id="element-119">
                Hoy: $22.990
              </p>
              <a href="https://www.bibliotecaprofesional.com/cart/48264948252842:1" onClick={() => trackFbEvent('InitiateCheckout', {
              content_name: 'Megapack Nutrición Carrera Resuelta',
              content_category: 'Nutrición Carrera Resuelta',
              value: 22990,
              currency: 'ARS'
            })} className="block bg-green-700 text-white text-center text-sm font-black rounded-lg py-4 no-underline hover:opacity-90 transition-opacity" data-id="element-120">
                → QUIERO MI MEGAPACK
              </a>
            </div>
          </div>

          {/* Premium */}
          <div className="flex flex-col rounded-xl p-6 border-2 border-green-700 shadow-sm" style={{
          backgroundColor: 'rgb(240,253,244)',
          boxShadow: 'rgba(34,197,94,0.15) 0px 0px 30px 0px'
        }} data-id="element-121">
            <div className="mb-2 text-2xl" data-id="element-122">🏆</div>
            <h3 className="text-base font-black text-green-700 m-0 mb-1" data-id="element-123">
              BIBLIOTECA PREMIUM NUTRICIÓN™ ⭐
            </h3>
            <p className="text-xs text-gray-500 m-0 mb-1" data-id="element-124">La más elegida</p>
            <p className="text-xs text-gray-500 m-0 mb-4" data-id="element-125">
              6 Tomos + 6 Bonos + Editables + Plantillas
              <br data-id="element-126" />
              (planificación, organización y herramientas)
            </p>
            <div className="mt-auto" data-id="element-127">
              <p className="text-sm text-gray-500 line-through m-0 mb-1" data-id="element-128">
                Antes: $102.990 ARS
              </p>
              <p className="text-3xl font-black text-green-700 m-0 mb-4" data-id="element-129">
                Hoy: $29.990
              </p>
              <a href="https://www.bibliotecaprofesional.com/cart/48322750480554:1" onClick={() => trackFbEvent('InitiateCheckout', {
              content_name: 'Biblioteca Premium Nutrición',
              content_category: 'Nutrición Carrera Resuelta',
              value: 29990,
              currency: 'ARS'
            })} className="block bg-green-700 text-white text-center text-sm font-black rounded-lg py-4 no-underline hover:opacity-90 transition-opacity" data-id="element-130">
                → QUIERO LA BIBLIOTECA PREMIUM
              </a>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-gray-600" data-id="element-131">
          <span data-id="element-132">🔒 Pago 100% seguro</span>
          <span data-id="element-133">⚡ Acceso en 5 minutos</span>
          <span data-id="element-134">🛡️ Garantía 7 días sin preguntas</span>
          <span data-id="element-135">💳 Mercado Pago | Tarjeta | Transferencia</span>
        </div>
      </div>
    </section>;
}